import { useState, useEffect } from 'react';
import { useConventionReportGuests } from '@/services/useConventionReport';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface GuestTableProps {
  periodId: number;
  defaultCategoryId?: string;
  defaultSearch?: string;
  categories?: any[];
}

export function GuestTable({ periodId, defaultCategoryId, defaultSearch, categories = [] }: GuestTableProps) {
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState(defaultSearch || '');
  const [cardCategoryId, setCardCategoryId] = useState(defaultCategoryId || 'all');

  // Reset category filter when defaultCategoryId prop changes
  useEffect(() => {
    if (defaultCategoryId !== undefined) {
      setCardCategoryId(defaultCategoryId);
      setPage(1);
    }
  }, [defaultCategoryId]);

  useEffect(() => {
    if (defaultSearch !== undefined) {
      setSearch(defaultSearch);
      setPage(1);
    }
  }, [defaultSearch]);
  
  const { data, isLoading } = useConventionReportGuests(periodId, {
    page: showAll ? 1 : page,
    per_page: showAll ? 9999 : 10,
    search,
    card_category_id: cardCategoryId === 'all' ? undefined : cardCategoryId,
  });

  return (
    <div className="space-y-4 mt-6 bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-2">
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <Input 
            placeholder="Cari nama, email, institusi..." 
            value={search} 
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }} 
            className="w-full sm:w-[250px]"
          />
          <Select value={cardCategoryId} onValueChange={(val) => { setCardCategoryId(val); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Semua Kategori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id.toString()}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama Lengkap</TableHead>
              <TableHead>Institusi / Instansi</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Status Email</TableHead>
              <TableHead>Status Cetak</TableHead>
              <TableHead>RFID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">Memuat data...</TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">Tidak ada data tamu/panitia</TableCell>
              </TableRow>
            ) : (
              data?.data.map((guest) => (
                <TableRow key={guest.id}>
                  <TableCell className="font-medium">{guest.full_name}</TableCell>
                  <TableCell>{guest.institution}</TableCell>
                  <TableCell>
                    <Badge style={{ backgroundColor: guest.card_category?.color_hex || '#000', color: '#fff' }}>
                      {guest.card_category?.name || 'Umum'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {guest.is_emailed ? <Badge variant="default">Terkirim</Badge> : <Badge variant="outline">Belum</Badge>}
                  </TableCell>
                  <TableCell>
                    {guest.is_printed ? <Badge variant="default">Tercetak</Badge> : <Badge variant="outline">Belum</Badge>}
                  </TableCell>
                  <TableCell>
                    {guest.rfid_number ? <Badge variant="outline" className="bg-blue-50 text-blue-700">Terdaftar</Badge> : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Total: {data?.meta.total || 0} data</span>
          {!showAll && data?.meta.last_page && data.meta.last_page > 1 && (
            <span>• Halaman {data.meta.current_page} dari {data.meta.last_page}</span>
          )}
          {data?.meta.total && data.meta.total > 10 && (
            <Button variant="link" size="sm" onClick={() => { setShowAll(!showAll); setPage(1); }} className="h-auto p-0 ml-1 text-primary hover:text-primary/80 font-medium">
              {showAll ? "(Tampilkan Lebih Sedikit)" : "(Tampilkan Semua)"}
            </Button>
          )}
        </div>
        {!showAll && (
          <div className="space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Sebelumnya
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === data?.meta.last_page || !data?.meta.last_page}
              onClick={() => setPage(p => p + 1)}
            >
              Selanjutnya
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
